<template>
  <div class="schedule-grid">
    <template v-for="planWeek in planWeeks" :key="`week-${planWeek.week}`">
      <!-- header -->
      <week-row-header :week-number="planWeek.week" :expandable="false" />

      <!-- week day grid -->
      <div class="week-group">
        <day-grid-button
          v-for="day in planWeek.days"
          :key="day.weekDay"
          :week-number="planWeek.week"
          :week-day-number="day.weekDay"
          :day-number="day.day"
          :stat="day.planDaySchedule.length"
          active-class="-active"
          :is-active="isSelected(day.day)"
          :aria-expanded="isSelected(day.day) ? 'true' : 'false'"
          :aria-controls="`plan-day-schedule-${day.day}`"
          :data-tour="day.planDaySchedule.length ? 'routine-has-workout' : ''"
          @toggle-selected="togglePlanDaySchedule"
        />
      </div>

      <!-- day schedule, expand / collapse, only one at a time -->
      <template v-for="day in planWeek.days" :key="`day-${day.day}`">
        <div v-if="isSelected(day.day)" :id="`plan-day-schedule-${day.day}`">
          <day-row-header
            :week-day-number="day.weekDay"
            :day-number="day.day"
            :is-expanded="isSelected(day.day)"
            :expand-id="`plan-day-schedule-${day.day}`"
            :enable-optional-button="!!(!readOnly && day.planDaySchedule.length)"
            :plan-day="day.planDay"
            @update-action="$emit('update-plan-day', $event)"
            @toggle-expanded="togglePlanDaySchedule"
          />
          <div v-if="day.planDaySchedule.length">
            <edit-plan-day-schedule
              v-for="scheduleItem in day.planDaySchedule"
              :key="`plan-day-schedule-${scheduleItem.id}`"
              :plan-day-schedule="scheduleItem"
              :enable-delete="!readOnly"
              :enable-optional-button="!readOnly"
              :enable-order-up="!readOnly && scheduleItem.order > 1"
              :enable-order-down="!readOnly && scheduleItem.order !== day.planDaySchedule.length"
              :reorder-disabled="disablePlanDayReordering === scheduleItem.planDayId"
              @update-action="$emit('update-plan-day-schedule', $event)"
              @delete-action="$emit('remove-plan-day-schedule', $event)"
              @reorder-start="handleReorder"
              @reorder-complete="reorderComplete"
              @reorder-error="reorderError"
            />
          </div>
          <div v-else class="day-detail-no-workout">
            <div>
              No workouts scheduled today.
              <span v-show="!readOnly">
                Search and add <a href="/videos">FB workouts</a> or
                <a href="/my/custom-workouts">custom workouts</a>.
              </span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import WeekRowHeader from './week-row-header.vue'
import DayGridButton from './day-grid-button.vue'
import DayRowHeader from './day-row-header.vue'
import EditPlanDaySchedule from './edit-plan-day-schedule.vue'

/**
 * Grid view for plan weeks.
 * - allows expanding only ONE day at a time
 * - expands within week row
 * - parent is owner of planWeeks
 * - emits the following events for parent to handle planWeeks manipulation:
 *    - update-plan-day
 *    - update-plan-day-schedule
 *    - remove-plan-day-schedule
 *    - reorder-plan-day-schedule
 */
export default defineComponent({
  name: 'PlanWeekGrid',
  components: { WeekRowHeader, DayGridButton, DayRowHeader, EditPlanDaySchedule },
  props: {
    // api output for routine plan-weeks
    // @see RoutinesApiController@getPlanWeeks
    // TODO: flow types for this, but it is really deep object :(
    planWeeks: {
      type: Array,
      required: true,
    },

    // removes any reordering, deleting, or required toggling making
    // the view fully read only (with expand / collapse)
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update-plan-day',
    'update-plan-day-schedule',
    'remove-plan-day-schedule',
    'reorder-plan-day-schedule',
  ],

  data() {
    return {
      // only single day can be expanded in grid view
      expandedDay: null,

      // used to disable ordering for entire plan day
      // value should be plan_day_id
      disablePlanDayReordering: 0,
    }
  },

  computed: {},

  methods: {
    /**
     * Toggle expand / collapse for the plan day schedule items (for the day)
     */
    togglePlanDaySchedule({ dayNumber }) {
      // remove selected day if already set
      if (this.expandedDay === dayNumber) {
        this.expandedDay = null
        return
      }

      // set day
      this.expandedDay = dayNumber
    },

    /**
     * check if selected by looking at map keys
     * dayNumber should be 1 - end of plan.  EX: 1-28 for 4 week plan.
     */
    isSelected(dayNumber) {
      return this.expandedDay === dayNumber
    },

    /**
     * Enable ordering for planDayId
     */
    disableReordering({ planDayId }) {
      this.disablePlanDayReordering = planDayId
    },

    /**
     * Clear out the planDayId lock / disabled state for reordering
     */
    reorderComplete() {
      this.disablePlanDayReordering = 0
    },

    /**
     * When reorder starts:
     * - disable all reorder buttons for the day
     * - instantly update UI (do not wait for XHR to finish)
     *
     * If there XHR is an error, it is handled by reorderError
     */
    handleReorder(event) {
      this.disablePlanDayReordering = event.planDayId
      this.$emit('reorder-plan-day-schedule', event)
    },

    /**
     * Clear out the disabled state and undo the schedule reordering
     * @param event
     */
    reorderError(event) {
      this.reorderComplete()
      this.$emit('reorder-plan-day-schedule', event)
    },
  },
})
</script>
